import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  getGitHubMigrationJobs,
  getGitHubMigrationTenants,
  refreshGitHubMigrationJobs,
  startGitHubMigrations,
  validateGitHubMigration,
  cancelGitHubMigrationJob,
} from "../../../APIs/githubMigrationAPI";
import { fetchRepositories } from "../../../APIs/AzureAPI";
import { BaseBox } from "../../../Common/UI/exports";

const POLLABLE_STATUSES = new Set(["queued", "in progress", "pending"]);
const TERMINAL_STATUSES = new Set(["completed", "failed", "cancelled", "canceled"]);
const DEFAULT_DESTINATION_URL = "https://github.com/VFCOM-ITaaP";
const CONFIGURED_PAT_SOURCE = "CONFIGURED";
const FINE_GRAINED_PAT_MESSAGE = "The configured migration PAT is fine-grained. GitHub Enterprise Importer requires a classic PAT; update the itaap-github-migration credential to use a classic PAT.";

const isPollableStatus = (status) => POLLABLE_STATUSES.has(String(status || "").toLowerCase());
const isTerminalStatus = (status) => TERMINAL_STATUSES.has(String(status || "").toLowerCase());
const githubPatRequestFields = () => ({ githubPatSource: CONFIGURED_PAT_SOURCE });

const errorMessage = (error, fallback) => {
  const data = error?.response?.data;
  const message = data?.message || data?.detail || data?.description || error?.message || fallback;
  return String(message);
};

function GitHubMigration() {
  const [repositories, setRepositories] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [configuredPat, setConfiguredPat] = useState(null);
  const [configuredPatError, setConfiguredPatError] = useState("");
  const [targetOrganizationUrl, setTargetOrganizationUrl] = useState(DEFAULT_DESTINATION_URL);
  const [selectedRepositoryIds, setSelectedRepositoryIds] = useState([]);
  const [repositorySearch, setRepositorySearch] = useState("");
  const [visibility, setVisibility] = useState("private");
  const [validation, setValidation] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [hiddenJobIds, setHiddenJobIds] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [jobToCancel, setJobToCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const requestInFlightRef = useRef(false);

  const selectedRepositories = useMemo(
    () => repositories.filter((repository) => selectedRepositoryIds.includes(repository.id)),
    [repositories, selectedRepositoryIds]
  );
  const filteredRepositories = useMemo(() => {
    const search = repositorySearch.trim().toLocaleLowerCase();
    if (!search) return repositories;
    return repositories.filter((repository) => (
      String(repository.name || "").toLocaleLowerCase().includes(search)
    ));
  }, [repositories, repositorySearch]);
  const visibleJobs = jobs.filter((job) => !hiddenJobIds.has(job.id));
  const clearableJobIds = jobs
    .filter((job) => isTerminalStatus(job.status) && !hiddenJobIds.has(job.id))
    .map((job) => job.id);
  const hasPollableJobs = jobs.some((job) => isPollableStatus(job.status));
  const hasPendingJobs = jobs.some((job) => String(job.status || "").toLowerCase() === "pending");
  const hasUnconfirmedSubmissions = jobs.some((job) => job.status === "Submitting");
  const fineGrainedPatSelected = Boolean(configuredPat?.fineGrainedPat);
  const hasGithubCredential = Boolean(configuredPat);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError("");
    const [repositoriesResult, jobsResult, githubTenantsResult] = await Promise.allSettled([
      fetchRepositories(),
      getGitHubMigrationJobs(),
      getGitHubMigrationTenants(),
    ]);

    if (repositoriesResult.status === "fulfilled") {
      setRepositories(repositoriesResult.value);
    } else {
      setError(errorMessage(repositoriesResult.reason, "Unable to load Azure repositories."));
    }
    if (jobsResult.status === "fulfilled") {
      setJobs(jobsResult.value);
    } else {
      setError((current) => current || errorMessage(jobsResult.reason, "Unable to load migration jobs."));
    }
    if (githubTenantsResult.status === "fulfilled") {
      const migrationPat = githubTenantsResult.value.find(
        (option) => option.source === CONFIGURED_PAT_SOURCE
      );
      setConfiguredPat(migrationPat || null);
      setConfiguredPatError(migrationPat
        ? ""
        : "The configured itaap-github-migration PAT is unavailable. Migration requests are disabled until the credential is configured.");
    } else {
      setConfiguredPat(null);
      setConfiguredPatError(errorMessage(
        githubTenantsResult.reason,
        "Unable to verify the configured itaap-github-migration PAT. Migration requests are disabled."
      ));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const refreshStatuses = useCallback(async () => {
    if (!hasGithubCredential || fineGrainedPatSelected || requestInFlightRef.current) return;
    requestInFlightRef.current = true;
    setRefreshing(true);
    try {
      const refreshedJobs = await refreshGitHubMigrationJobs(
        githubPatRequestFields()
      );
      setJobs(refreshedJobs);
      setError("");
    } catch (refreshError) {
      setError(errorMessage(refreshError, "Unable to refresh GEI migration status."));
    } finally {
      requestInFlightRef.current = false;
      setRefreshing(false);
    }
  }, [hasGithubCredential, fineGrainedPatSelected]);

  useEffect(() => {
    if (!hasGithubCredential || fineGrainedPatSelected || !hasPollableJobs) return undefined;
    const intervalId = window.setInterval(refreshStatuses, 10000);
    return () => window.clearInterval(intervalId);
  }, [fineGrainedPatSelected, hasGithubCredential, hasPollableJobs, refreshStatuses]);

  const handleRepositoryToggle = (repositoryId) => {
    setValidation(null);
    setNotice("");
    setSelectedRepositoryIds((selected) => (
      selected.includes(repositoryId)
        ? selected.filter((id) => id !== repositoryId)
        : [...selected, repositoryId]
    ));
  };

  const handleValidate = async () => {
    if (requestInFlightRef.current) return;
    requestInFlightRef.current = true;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const result = await validateGitHubMigration({
        repositoryIds: selectedRepositoryIds,
        ...githubPatRequestFields(),
        targetOrganizationUrl,
        targetRepoVisibility: visibility,
      });
      setValidation(result);
      setNotice(result.message);
    } catch (validationError) {
      setValidation(null);
      setError(errorMessage(validationError, "Unable to validate GEI migration access."));
    } finally {
      requestInFlightRef.current = false;
      setBusy(false);
    }
  };

  const handleStart = async () => {
    if (requestInFlightRef.current) return;
    requestInFlightRef.current = true;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const startedJobs = await startGitHubMigrations({
        repositoryIds: selectedRepositoryIds,
        ...githubPatRequestFields(),
        targetOrganizationUrl,
        targetRepoVisibility: visibility,
      });
      setJobs((current) => {
        const startedIds = new Set(startedJobs.map((job) => job.id));
        return [...startedJobs, ...current.filter((job) => !startedIds.has(job.id))];
      });
      setValidation(null);
      const failedCount = startedJobs.filter((job) => job.status === "Failed").length;
      const queuedCount = startedJobs.filter((job) => job.status === "Pending").length;
      const acceptedCount = startedJobs.length - failedCount - queuedCount;
      setReviewOpen(false);
      setNotice([
        acceptedCount > 0 && `GitHub Enterprise Importer accepted ${acceptedCount} migration(s).`,
        queuedCount > 0 && `${queuedCount} migration(s) are queued and will be submitted as GEI capacity becomes available.`,
      ].filter(Boolean).join(" "));
      setError(failedCount > 0
        ? `${failedCount} migration(s) failed to start. See each job's details in the dashboard.`
        : "");
    } catch (startError) {
      setError(errorMessage(startError, "Unable to start GEI migrations."));
    } finally {
      requestInFlightRef.current = false;
      setBusy(false);
    }
  };

  const clearFinishedJobs = () => {
    setHiddenJobIds((current) => new Set([...current, ...clearableJobIds]));
    setNotice(`Cleared ${clearableJobIds.length} finished migration(s) from this dashboard. Active migrations were kept.`);
  };

  const handleCancelClick = (job) => {
    setJobToCancel(job);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!jobToCancel || cancelling) return;
    setCancelling(true);
    setError("");
    try {
      const updatedJob = await cancelGitHubMigrationJob(jobToCancel.id);
      setJobs((current) =>
        current.map((job) => (job.id === updatedJob.id ? updatedJob : job))
      );
      setNotice(`Migration cancelled: ${jobToCancel.sourceRepositoryName}`);
      setCancelDialogOpen(false);
      setJobToCancel(null);
    } catch (cancelError) {
      setError(errorMessage(cancelError, `Failed to cancel migration: ${jobToCancel.sourceRepositoryName}`));
    } finally {
      setCancelling(false);
    }
  };

  const allSelected = (repositoryId) => selectedRepositoryIds.includes(repositoryId);

  return (
    <BaseBox sx={{ maxWidth: 1100, width: "100%", mx: "auto" }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Azure DevOps to GitHub Enterprise Importer
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Select any number of Azure repositories. Up to five migrations run at a time; additional repositories are queued and submitted as capacity becomes available. GEI migrates Git history and supported Azure DevOps pull request data.
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        GitHub Enterprise Importer uses the configured itaap-github-migration PAT. The secret is read by the backend and is never sent to or stored by this page.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {configuredPatError && <Alert severity="error" sx={{ mb: 2 }}>{configuredPatError}</Alert>}
      {fineGrainedPatSelected && <Alert severity="error" sx={{ mb: 2 }}>{FINE_GRAINED_PAT_MESSAGE}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}

      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <TextField
              label="GitHub destination organization URL"
              type="url"
              value={targetOrganizationUrl}
              onChange={(event) => {
                setTargetOrganizationUrl(event.target.value);
                setValidation(null);
              }}
              fullWidth
              sx={{ mb: 2 }}
              helperText="Enter the GitHub organization URL, for example https://github.com/VFCOM-ITaaP."
            />
            <Alert severity="info" sx={{ mb: 2 }}>
              {configuredPat
                ? `Using the configured ${configuredPat.name} credential. The PAT value is not sent by the browser.`
                : "Checking the configured migration PAT..."}
            </Alert>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="github-migration-visibility-label">Target visibility</InputLabel>
              <Select
                labelId="github-migration-visibility-label"
                value={visibility}
                label="Target visibility"
                onChange={(event) => {
                  setVisibility(event.target.value);
                  setValidation(null);
                }}
              >
                <MenuItem value="private">Private</MenuItem>
                <MenuItem value="internal">Internal</MenuItem>
                <MenuItem value="public">Public</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {loading
                ? "Loading Azure repositories..."
                : `Azure repositories retrieved: ${repositories.length}`}
            </Typography>
            <Typography variant="subtitle1" fontWeight={600}>
              Azure repositories ({selectedRepositoryIds.length} selected)
            </Typography>
            <TextField
              label="Search Azure repositories"
              value={repositorySearch}
              onChange={(event) => setRepositorySearch(event.target.value)}
              fullWidth
              size="small"
              sx={{ mt: 1, mb: 1 }}
              inputProps={{ "aria-label": "Search Azure repositories" }}
            />
            <TableContainer sx={{ maxHeight: 340, mt: 1 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>Repository</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRepositories.map((repository) => (
                    <TableRow key={repository.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allSelected(repository.id)}
                          onChange={() => handleRepositoryToggle(repository.id)}
                          inputProps={{ "aria-label": `Select ${repository.name}` }}
                        />
                      </TableCell>
                      <TableCell>{repository.name}</TableCell>
                    </TableRow>
                  ))}
                  {filteredRepositories.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2}>
                        {repositories.length === 0
                          ? "No Azure repositories are available."
                          : "No repositories match your search."}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <FormControlLabel
              control={<Checkbox checked disabled />}
              label="Continue migration when non-critical items fail"
              sx={{ mt: 1, mb: 1 }}
            />
            <BaseBox sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                variant="outlined"
                onClick={handleValidate}
                disabled={busy || refreshing || fineGrainedPatSelected || !hasGithubCredential || !targetOrganizationUrl.trim() || selectedRepositoryIds.length === 0}
              >
                {busy ? "Working..." : "Validate"}
              </Button>
              <Button
                variant="contained"
                onClick={() => setReviewOpen(true)}
                disabled={busy || refreshing || fineGrainedPatSelected || !validation?.valid || !hasGithubCredential || selectedRepositories.length === 0}
              >
                Review migration
              </Button>
              <Button onClick={refreshStatuses} disabled={busy || refreshing || fineGrainedPatSelected || !hasGithubCredential || !hasPollableJobs}>
                {refreshing ? "Refreshing..." : "Refresh status"}
              </Button>
            </BaseBox>
          </Paper>

          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>Migration dashboard</Typography>
          <Button
            onClick={clearFinishedJobs}
            disabled={clearableJobIds.length === 0}
            sx={{ mb: 1 }}
            title="Hides finished rows from this dashboard only; it does not delete migration jobs."
          >
            Clear completed/failed ({clearableJobIds.length})
          </Button>
          {clearableJobIds.length > 0 && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              This only clears finished rows from the dashboard; active jobs and GitHub migration records are unchanged.
            </Typography>
          )}
          {hasGithubCredential && !fineGrainedPatSelected && hasPollableJobs && (
            <Alert severity="info" sx={{ mb: 1 }}>
              {hasPendingJobs
                ? "Migration statuses refresh automatically every second while this page is open. PAF also checks GEI in the background and submits pending repositories as capacity becomes available."
                : "Active migration statuses refresh automatically every second."}
            </Alert>
          )}
          {!hasGithubCredential && hasPollableJobs && (
            <Alert severity="info" sx={{ mb: 1 }}>
              Configure the itaap-github-migration PAT to refresh active jobs.
            </Alert>
          )}
          {hasUnconfirmedSubmissions && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              A submission was interrupted before GitHub returned a migration ID. Check the destination repository before retrying to avoid starting a duplicate migration.
            </Alert>
          )}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Azure repository</TableCell>
                  <TableCell>GitHub destination</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Details</TableCell>
                  <TableCell>Migration log</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleJobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell>{job.sourceRepositoryName}</TableCell>
                    <TableCell>
                      <Link href={job.targetRepositoryUrl} target="_blank" rel="noreferrer">
                        {job.targetOrganization}/{job.targetRepositoryName}
                      </Link>
                    </TableCell>
                    <TableCell>{job.status}</TableCell>
                    <TableCell>{job.message}</TableCell>
                    <TableCell>
                      {job.migrationLogUrl
                        && job.migrationLogUrl !== `${job.targetRepositoryUrl}/issues` && (
                          <Link href={job.migrationLogUrl} target="_blank" rel="noreferrer" sx={{ mr: 1 }}>
                            Download migration log
                          </Link>
                        )}
                      <Link href={`${job.targetRepositoryUrl}/issues`} target="_blank" rel="noreferrer">
                        Migration log issue
                      </Link>
                    </TableCell>
                    <TableCell>
                      {job.status === "Submitting" && (
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => handleCancelClick(job)}
                          disabled={cancelling}
                        >
                          {cancelling && jobToCancel?.id === job.id ? (
                            <>
                              <CircularProgress size={16} sx={{ mr: 0.5 }} />
                              Cancelling...
                            </>
                          ) : (
                            "Cancel"
                          )}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {visibleJobs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6}>
                      {jobs.length === 0
                        ? "No migration jobs have been submitted."
                        : "No visible migration jobs. Active jobs are never cleared."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Dialog
            open={reviewOpen}
            onClose={() => {
              if (!busy) setReviewOpen(false);
            }}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Review migration batch</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Review the repositories and destination before starting this migration batch.
              </DialogContentText>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Selected repositories</Typography>
              <List dense>
                {selectedRepositories.map((repository) => (
                  <ListItem key={repository.id} disableGutters>
                    <ListItemText
                      primary={repository.name}
                    />
                  </ListItem>
                ))}
              </List>
              <Typography variant="body2">
                <strong>GitHub organization:</strong> {targetOrganizationUrl}
              </Typography>
              <Typography variant="body2">
                <strong>Repository visibility:</strong> {visibility.charAt(0).toUpperCase() + visibility.slice(1)}
              </Typography>
              {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setReviewOpen(false)} disabled={busy}>Back</Button>
              <Button
                variant="contained"
                onClick={handleStart}
                disabled={busy || refreshing || !validation?.valid || selectedRepositories.length === 0}
              >
                {busy ? "Starting..." : "Confirm and start migration"}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog
            open={cancelDialogOpen}
            onClose={() => {
              if (!cancelling) setCancelDialogOpen(false);
            }}
          >
            <DialogTitle>Cancel Migration</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to cancel the migration for <strong>{jobToCancel?.sourceRepositoryName}</strong>?
              </DialogContentText>
              <DialogContentText sx={{ mt: 1, color: "warning.main" }}>
                The "Submitting" status means the migration request has not yet been confirmed by GitHub. Cancelling may prevent the migration from being queued.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCancelDialogOpen(false)} disabled={cancelling}>Keep</Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleCancelConfirm}
                disabled={cancelling}
              >
                {cancelling ? "Cancelling..." : "Cancel Migration"}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </BaseBox>
  );
}

export default GitHubMigration;
